import { fetchRemoteConfig } from '@/hotdesk-ui/common-classes/firebase.service';
import { RemoteConfigKeys } from '@/hotdesk-ui/enum/remote-keys';
import { loadWebSubscriptionPlansOrNull } from '@/hotdesk-ui/services/subscription-availability.service';

export const BOOKING_CHECKOUT_FROM = 'booking_checkout';

export const CHECKOUT_UPGRADE_STORAGE = {
    OPEN_PLANS: 'checkout_upgrade_open_plans',
} as const;

export type CheckoutUpgradeCountryConfig = {
    _country: string;
    packages: number[];
    discount: number;
};

export type CheckoutUpgradeEligibility = {
    /** subscriptionListingOfferToggle && listing covered (or QA bypass). */
    eligible: boolean;
    countryConfig: CheckoutUpgradeCountryConfig | null;
};

const isProductionWeb = (): boolean =>
    process.env.NEXT_PUBLIC_SELF_URL === 'https://www.hotdesk.com/';

const normalizeCountry = (value: string): string =>
    value.trim().toLowerCase().replace(/\s+/g, ' ');

/** UAE aliases used in geo / listing data vs RC `_country` labels. */
const COUNTRY_ALIASES: Record<string, string[]> = {
    uae: ['uae', 'ae', 'united arab emirates', 'the united arab emirates'],
    egypt: ['egypt', 'eg'],
};

const countriesMatch = (configCountry: string, candidate: string): boolean => {
    const a = normalizeCountry(configCountry);
    const b = normalizeCountry(candidate);
    if (a === b) return true;
    for (const aliases of Object.values(COUNTRY_ALIASES)) {
        if (aliases.includes(a) && aliases.includes(b)) return true;
    }
    return false;
};

export const parseCheckoutUpgradeConfig = (
    raw: unknown
): CheckoutUpgradeCountryConfig[] => {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((entry) => {
            if (!entry || typeof entry !== 'object') return null;
            const obj = entry as Record<string, unknown>;
            const country = obj._country;
            const packages = obj.packages;
            const discount = obj.discount;
            if (typeof country !== 'string' || !country.trim()) return null;
            if (!Array.isArray(packages) || packages.length === 0) return null;
            const packageIds = packages
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id));
            if (packageIds.length === 0) return null;
            const discountValue = Number(discount);
            if (!Number.isFinite(discountValue) || discountValue < 0) return null;
            return {
                _country: country.trim(),
                packages: packageIds,
                discount: discountValue,
            } satisfies CheckoutUpgradeCountryConfig;
        })
        .filter((entry): entry is CheckoutUpgradeCountryConfig => entry != null);
};

export const matchCheckoutUpgradeConfig = (
    configs: CheckoutUpgradeCountryConfig[],
    countryName: string | null | undefined
): CheckoutUpgradeCountryConfig | null => {
    if (!countryName || configs.length === 0) return null;
    return (
        configs.find((cfg) => countriesMatch(cfg._country, countryName)) ?? null
    );
};

export const loadCheckoutUpgradeConfig = async (): Promise<
    CheckoutUpgradeCountryConfig[]
> => {
    try {
        const raw = await fetchRemoteConfig(
            RemoteConfigKeys.WEB_CHECKOUT_UPGRADE_POPUP,
            'object'
        );
        return parseCheckoutUpgradeConfig(raw);
    } catch {
        return [];
    }
};

/**
 * Option A (web): enableSubscriptionPlans =
 * subscription_offering_web_page_switch + non-empty offerings packages.
 */
export const loadEnableSubscriptionPlans = async (
    languageCode: string
): Promise<boolean> => {
    const plans = await loadWebSubscriptionPlansOrNull(languageCode);
    return !!plans && Array.isArray(plans.packages) && plans.packages.length > 0;
};

export const loadSubscriptionListingOfferToggle = async (
    languageCode: string
): Promise<boolean> => {
    try {
        const listingOfferToggle = (await fetchRemoteConfig(
            RemoteConfigKeys.SUBSCRIPTION_LISTING_OFFER_TOGGLE,
            'boolean'
        )) as boolean;
        if (!listingOfferToggle) return false;
        return loadEnableSubscriptionPlans(languageCode);
    } catch {
        return false;
    }
};

export const parseCoveredListingIds = (raw: unknown): number[] => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
    const listingIds = (raw as { listingIds?: unknown }).listingIds;
    if (!Array.isArray(listingIds)) return [];
    return listingIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));
};

export const loadCoveredListingIds = async (): Promise<number[]> => {
    try {
        const raw = await fetchRemoteConfig(
            RemoteConfigKeys.SUBSCRIPTION_LISTING_OFFER_LISTINGS,
            'object'
        );
        return parseCoveredListingIds(raw);
    } catch {
        return [];
    }
};

/**
 * Listing applicable via SUBSCRIPTION_LISTING_OFFER_LISTINGS.listingIds.
 *
 * Prod: listing must be in that list (empty list → not applicable).
 * QA / local: skip this check so any listing can preview the offer.
 */
export const isListingCoveredForOffer = (
    listingId: number | string | null | undefined,
    coveredListingIds: number[]
): boolean => {
    if (listingId == null || listingId === '') return false;
    const id = Number(listingId);
    if (!Number.isFinite(id)) return false;

    // Local + QA (anything that is not www.hotdesk.com): don't gate on listingIds.
    if (!isProductionWeb()) return true;

    return coveredListingIds.includes(id);
};

/**
 * subscriptionListingOfferToggle && listing covered by RC listingIds.
 * Does not check login / membership — callers hide for existing members.
 */
export const loadListingSubscriptionOfferEligibility = async (args: {
    languageCode: string;
    listingId: number | string | null | undefined;
}): Promise<boolean> => {
    const [listingOfferToggle, coveredListingIds] = await Promise.all([
        loadSubscriptionListingOfferToggle(args.languageCode),
        loadCoveredListingIds(),
    ]);
    return (
        listingOfferToggle &&
        isListingCoveredForOffer(args.listingId, coveredListingIds)
    );
};

/**
 * Full gate for membership offer surfaces (listing banner + checkout upsell):
 * subscriptionListingOfferToggle && coveredListingIds.contains(listingId)
 * (+ country packages from web_checkout_upgrade_popup for popup/discount).
 * Does not check login / membership — callers hide for existing members.
 */
export const loadCheckoutUpgradeEligibility = async (args: {
    languageCode: string;
    listingId: number | string | null | undefined;
    countryName: string | null | undefined;
}): Promise<CheckoutUpgradeEligibility> => {
    const [listingOfferToggle, coveredListingIds, upgradeConfigs] =
        await Promise.all([
            loadSubscriptionListingOfferToggle(args.languageCode),
            loadCoveredListingIds(),
            loadCheckoutUpgradeConfig(),
        ]);

    const countryConfig = matchCheckoutUpgradeConfig(
        upgradeConfigs,
        args.countryName
    );
    const listingCovered = isListingCoveredForOffer(
        args.listingId,
        coveredListingIds
    );

    return {
        eligible: listingOfferToggle && listingCovered,
        countryConfig,
    };
};

export const buildCheckoutUpgradeUrl = (
    packageId: string | number,
    planName?: string
): string => {
    const params = new URLSearchParams({ from: BOOKING_CHECKOUT_FROM });
    if (planName) params.set('plan', planName);
    return `/subscriptions/${packageId}?${params.toString()}`;
};

export const saveCheckoutUpgradeOpenPlansFlag = (): void => {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(CHECKOUT_UPGRADE_STORAGE.OPEN_PLANS, '1');
};

export const consumeCheckoutUpgradeOpenPlansFlag = (): boolean => {
    if (typeof window === 'undefined') return false;
    const value = sessionStorage.getItem(CHECKOUT_UPGRADE_STORAGE.OPEN_PLANS);
    if (value !== '1') return false;
    sessionStorage.removeItem(CHECKOUT_UPGRADE_STORAGE.OPEN_PLANS);
    return true;
};

export const peekCheckoutUpgradeOpenPlansFlag = (): boolean => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(CHECKOUT_UPGRADE_STORAGE.OPEN_PLANS) === '1';
};
